import type { FieldValues, UseFormReturn } from "react-hook-form";

import { t } from "@lingui/core/macro";
import { useCallback, useEffect, useRef } from "react";

import { useDialogStore } from "@/dialogs/store";
import { useConfirm } from "@/hooks/use-confirm";

interface UseFormBlockerOptions {
  shouldBlock?: () => boolean;
  shouldBlockSilently?: () => boolean;
}

export function useFormBlocker<T extends FieldValues>(form: UseFormReturn<T>, options?: UseFormBlockerOptions) {
  const confirm = useConfirm();
  const closeDialog = useDialogStore((state) => state.closeDialog);
  const setCloseGuard = useDialogStore((state) => state.setCloseGuard);

  // Subscribe to formState changes by reading during render (react-hook-form requirement)
  const { isDirty, isSubmitting } = form.formState;
  const confirmationPendingRef = useRef(false);
  const shouldBlockRef = useRef<() => boolean>(() => false);
  const shouldBlockSilentlyRef = useRef<(() => boolean) | undefined>(undefined);

  shouldBlockRef.current = options?.shouldBlock ?? (() => isDirty && !isSubmitting);
  shouldBlockSilentlyRef.current = options?.shouldBlockSilently;

  const shouldBlock = useCallback(() => shouldBlockRef.current(), []);

  const requestClose = useCallback(async () => {
    if (!shouldBlock()) {
      closeDialog();
      return;
    }

    // Prevent overlapping confirmation prompts
    if (confirmationPendingRef.current) return;
    confirmationPendingRef.current = true;

    const confirmed = await confirm(t`Are you sure you want to close this dialog?`, {
      description: t`You have unsaved changes that will be lost.`,
      confirmText: t`Leave`,
      cancelText: t`Stay`,
    });

    confirmationPendingRef.current = false;

    // Force-close to bypass the guard and avoid double confirmation
    if (confirmed) (closeDialog as (force?: boolean) => void)(true);
  }, [shouldBlock, closeDialog, confirm]);

  // Register a close guard on the dialog store so escape / outside-click
  // dismissals are intercepted at the Dialog.Root level instead of via
  // legacy React event-handler props that produce DOM warnings.
  useEffect(() => {
    // Track overlapping guard invocations to avoid duplicate prompts
    let guardActive = false;

    const guard = async () => {
      // Silently block close when shouldBlockSilently is active (e.g. pending mutation)
      if (shouldBlockSilentlyRef.current?.()) return false;

      if (!shouldBlock()) return true;

      // Prevent overlapping confirmation prompts from rapid close attempts
      if (guardActive) return false;
      guardActive = true;

      const confirmed = await confirm(t`Are you sure you want to close this dialog?`, {
        description: t`You have unsaved changes that will be lost.`,
        confirmText: t`Leave`,
        cancelText: t`Stay`,
      });

      guardActive = false;
      return confirmed;
    };

    setCloseGuard(guard);
    return () => {
      setCloseGuard(null);
    };
  }, [shouldBlock, confirm, setCloseGuard]);

  return { requestClose, blockEvents: {} as Record<string, never> };
}
